import { useEntity } from '@app/hooks/useEntity';
import { Address } from '@app/model/Address';
import { useCountryStore } from '@app/store/entityStore';
import { displayAddress } from '@app/utils/functions';
import {
  Button,
  Content,
  Form,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  Icon,
  MenuToggleStatus,
  TextInput
} from '@patternfly/react-core';
import { Modal, ModalVariant } from '@patternfly/react-core/deprecated';
import { ExclamationCircleIcon, PencilAltIcon } from '@patternfly/react-icons';
import { TypeaheadSelect } from '@patternfly/react-templates';
import React, { useState } from 'react';

export interface FormAddressProps {
  onChange(_event: any, address: Address): void;
  value: Address | undefined | null;
  isDisabled?: boolean;
  emptyMessage?: string;
  id?: string;
  name?: string;
}

type ValidationStatus = 'success' | 'warning' | 'error' | 'default';
type ValidationState = Record<string, ValidationStatus>;

/**
 * The fields that must be filled in before the address can be confirmed.
 *
 * **THE POSTCODE IS NOT ONE OF THEM, AND USED TO BE.** An address legitimately has no postcode:
 * where the country is covered by the reference data and no code in the address matches any it
 * has, the service writes none and leaves the text in the lines, which is what stops a house
 * number - one Lisbon class held `55` - landing in a column people search on. Countries the
 * GeoNames export does not cover at all (Tunisia, Greece, the UAE and six more) never get one.
 *
 * While it was required, `checkValid` refused to enable Confirm for any address without one - so
 * exactly the addresses the parse deliberately leaves postcode-less were the ones nobody could
 * edit at all, on any field, without first inventing a code. See README-postcodes §6, and note
 * that no format validation belongs here either: a human typing a postcode is a statement, the
 * service does not overrule it, and a rule here would reject codes these rules do not know.
 */
const REQUIRED_FIELDS = ['line1', 'city', 'country'] as const;

const INITIAL_VALIDATION: ValidationState = {
  line1: 'default',
  city: 'default',
  country: 'default'
};

const emptyAddress : Partial<Address> = { }

export const FormAddress: React.FunctionComponent<FormAddressProps> = ({
  isDisabled = false,
  emptyMessage = 'No address recorded.',
  value = emptyAddress,
  ...props
}) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [address, setAddress] = useState<Partial<Address>>(() => value || emptyAddress);
  const [originalAddress, setOriginalAddress] = useState<Partial<Address>>({});
  const [validated, setValidated] = useState<ValidationState>(INITIAL_VALIDATION);
  const {items: countries} = useEntity(useCountryStore);

  // 1. Dynamic Handle Change
  const handleChange = (_event: React.FormEvent<HTMLInputElement>, val: string) => {
    const id = (_event.target as HTMLInputElement).id;
    const fieldName = id.replace('form-address-', ''); // Extracts 'line1', 'city', etc.

    setAddress((prev) => ({ ...prev, [fieldName]: val }));

    // Only validate required fields
    if ((REQUIRED_FIELDS as readonly string[]).includes(fieldName)) {
      validateField(fieldName, val);
    }
  };

  const handleCountryChange = (_event: any, val: string | number | undefined) => {
    const country = countries.find((c) => c.alpha2 === val);
    setAddress((prev) => ({ ...prev, country }));
    validateField('country', country);
  };

  // 2. Simplified Validation Logic
  const validateField = (field: string, val: any) => {
    setValidated((prev) => ({
      ...prev,
      [field]: val ? 'success' : 'error'
    }));
  };

  const checkValid = () => {
    return Object.values(validated).every((status) => status === 'success');
  };

  // 3. Modal Handlers
  const onOpenModal = (_event: any) => {
    const currentAddress = value || emptyAddress;
    setAddress(currentAddress);
    setOriginalAddress(currentAddress); // Fixed: Captures current value, not stale state

    // Pre-validate existing data so the confirm button enables correctly
    setValidated({
      line1: currentAddress.line1 ? 'success' : 'default',
      city: currentAddress.city ? 'success' : 'default',
      country: currentAddress.country ? 'success' : 'default'
    });

    setModalOpen(true);
  };

  const onCancel = (_event: any) => {
    setAddress(originalAddress);
    setModalOpen(false);
  };

  const onConfirm = (_event: any) => {
    props.onChange(_event, address as Address);
    setModalOpen(false);
  };

  // 4. Render Helpers
  const renderEditButton = () => {
    if (isDisabled) return null;
    return (
      <Button
        icon={<Icon size='lg'><PencilAltIcon /></Icon>}
        variant="plain"
        isInline
        onClick={onOpenModal}
        isDisabled={isDisabled}
      />
    );
  };

  const renderAddressText = () => {
    const addressString = displayAddress(value as Address);
    return (
      <Content component="p" className={!addressString ? 'empty-address-output-text' : ''}>
        {addressString || emptyMessage} {renderEditButton()}
      </Content>
    );
  };

  return (
    <React.Fragment>
      <Content>
        {!isModalOpen && renderAddressText()}
      </Content>

      <Modal
        variant={ModalVariant.small}
        title="Edit Address"
        description="Enter the address details."
        isOpen={isModalOpen}
        onClose={onCancel}
        actions={[
          <Button key="create" variant="primary" form="address-form" onClick={onConfirm} isDisabled={!checkValid()}>
            Confirm
          </Button>,
          <Button key="cancel" variant="link" onClick={onCancel}>
            Cancel
          </Button>
        ]}
      >
        <Form id="address-form">
          <FormGroup label="Line 1" isRequired fieldId="form-address-line1">
            <TextInput isRequired type="text" id="form-address-line1" name="form-address-line1"
              value={address.line1 || ''} onChange={handleChange} validated={validated.line1} />
            {validated.line1 === 'error' && (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem icon={<ExclamationCircleIcon />} variant="error">
                    Cannot be blank
                  </HelperTextItem>
                </HelperText>
              </FormHelperText>
            )}
          </FormGroup>
          
          <FormGroup label="Line 2" fieldId="form-address-line2">
            <TextInput type="text" id="form-address-line2" name="form-address-line2"
              value={address.line2 || ''} onChange={handleChange} />
          </FormGroup>
          
          <FormGroup label="Line 3" fieldId="form-address-line3">
            <TextInput type="text" id="form-address-line3" name="form-address-line3"
              value={address.line3 || ''} onChange={handleChange} />
          </FormGroup>
          
          <FormGroup label="City" isRequired fieldId="form-address-city">
            <TextInput isRequired type="text" id="form-address-city" name="form-address-city"
              value={address.city || ''} onChange={handleChange} validated={validated.city} />
            {validated.city === 'error' && (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem icon={<ExclamationCircleIcon />} variant="error">
                    Cannot be blank
                  </HelperTextItem>
                </HelperText>
              </FormHelperText>
            )}
          </FormGroup>
          
          <FormGroup label="Country" isRequired fieldId="form-address-country">
            <TypeaheadSelect
              initialOptions={countries.map((c) => ({ content: `${c.flag} ${c.name}`, value: c.alpha2, selected: c.alpha2 === address.country?.alpha2 }))}
              placeholder="Select a country" id='form-address-country'
              noOptionsFoundMessage={(filter) => `No country was found for "${filter}"`}
              onClearSelection={() => handleCountryChange(undefined, undefined)}
              toggleProps={{ status: validated.country as MenuToggleStatus }}
              onSelect={handleCountryChange}
            />
            {validated.country === 'error' && (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem icon={<ExclamationCircleIcon />} variant="error">
                    Cannot be blank
                  </HelperTextItem>
                </HelperText>
              </FormHelperText>
            )}
          </FormGroup>
          
          {/* Optional, and left blank on purpose more often than not - see REQUIRED_FIELDS. */}
          <FormGroup label="Postcode" fieldId="form-address-postcode">
            <TextInput type="text" id="form-address-postcode" name="form-address-postcode"
              value={address.postcode || ''} onChange={handleChange} />
            <FormHelperText>
              <HelperText>
                <HelperTextItem>
                  Leave blank if the address has none, or if none could be read from it. The service
                  does not guess one.
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>
        </Form>
      </Modal>
    </React.Fragment>
  );
};